package com.fantasytenis.shared.data.notifications.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import com.fantasytenis.shared.domain.notifications.entity.NotificationEntity
import com.fantasytenis.shared.domain.notifications.entity.NotificationType
import com.fantasytenis.shared.domain.notifications.entity.NotificationPage

/**
 * DTO for a notification from the API.
 */
@Serializable
data class NotificationDto(
    val id: String,
    @SerialName("userId") val userId: String,
    val type: String,
    val title: String,
    val message: String,
    val read: Boolean,
    @SerialName("contestId") val contestId: String? = null,
    @SerialName("entryId") val entryId: String? = null,
    val metadata: JsonObject? = null,
    @SerialName("createdAt") val createdAt: String,
    @SerialName("updatedAt") val updatedAt: String
) {
    fun toEntity(): NotificationEntity = NotificationEntity(
        id = id,
        userId = userId,
        type = parseNotificationType(type),
        title = title,
        message = message,
        read = read,
        contestId = contestId,
        entryId = entryId,
        metadata = null, // Simplified - could deserialize JsonObject if needed
        createdAt = createdAt,
        updatedAt = updatedAt
    )

    private fun parseNotificationType(type: String): NotificationType {
        return try {
            NotificationType.valueOf(type)
        } catch (e: IllegalArgumentException) {
            NotificationType.GENERAL
        }
    }
}

/**
 * Response wrapper for notifications list.
 */
@Serializable
data class NotificationsResponseDto(
    val status: Int,
    val error: NotificationErrorDto? = null,
    val data: NotificationsDataDto? = null
)

@Serializable
data class NotificationsDataDto(
    val notifications: List<NotificationDto>,
    val total: Int,
    @SerialName("hasMore") val hasMore: Boolean
) {
    fun toPage(): NotificationPage = NotificationPage(
        notifications = notifications.map { it.toEntity() },
        total = total,
        hasMore = hasMore
    )
}

/**
 * Response wrapper for single notification.
 */
@Serializable
data class NotificationResponseDto(
    val status: Int,
    val error: NotificationErrorDto? = null,
    val data: NotificationDto? = null
)

/**
 * Response wrapper for unread count.
 */
@Serializable
data class UnreadCountResponseDto(
    val status: Int,
    val error: NotificationErrorDto? = null,
    val data: UnreadCountDataDto? = null
)

@Serializable
data class UnreadCountDataDto(
    val count: Int
)

/**
 * Generic success response.
 */
@Serializable
data class NotificationSuccessResponseDto(
    val status: Int,
    val error: NotificationErrorDto? = null,
    val data: NotificationSuccessDataDto? = null
)

@Serializable
data class NotificationSuccessDataDto(
    val success: Boolean,
    val message: String
)

@Serializable
data class NotificationErrorDto(
    val type: String,
    val message: String
)
